/* ════════════════════════════════════════════════════════════════
   Tessera Diamonds — GSAP / Lenis animation engine
   Ported from landing-page-preview.html. Runs on every page (deferred),
   so every step is guarded to no-op when its target elements are absent.
   ════════════════════════════════════════════════════════════════ */
(function () {
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

  gsap.registerPlugin(ScrollTrigger);

  /* LENIS — smooth scroll for the marketing pages only.
     Skip it on pages that embed third-party widgets (.widget-section) or the
     diamond product detail (.td-product): Lenis intercepts wheel/touch to do
     its own scrolling, and on those heavy embedded-widget pages that can
     leave the page feeling unscrollable. Native scroll there is bulletproof,
     and GSAP ScrollTrigger reveals still work on native scroll. The FAQ /
     Education anchor-scroll helpers already fall back to scrollIntoView when
     window.tdLenis is absent. */
  var skipLenis = document.querySelector('.widget-section')
    || document.querySelector('.td-product')
    || document.querySelector('#diamond-proxy-root'); // any page hosting an embedded widget
  if (typeof Lenis !== 'undefined' && !skipLenis) {
    const lenis = new Lenis({ lerp: 0.08, smooth: true });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(t => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
    window.tdLenis = lenis; // exposed for page scripts (e.g. FAQ sidebar smooth-scroll)
  }

  /* HERO ENTRANCE — only when the hero is on the page */
  const heroIds = ['#h-h1', '#h-sub', '#h-act', '#h-sc'];
  if (document.querySelector('#h-h1')) {
    gsap.set(heroIds, { opacity: 0 });
    const htl = gsap.timeline({ delay: 0.3 });
    htl
      .fromTo('#h-h1', { opacity: 0, y: 44, filter: 'blur(26px)' }, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 1.1, ease: 'power3.out' })
      .fromTo('#h-sub', { opacity: 0, y: 28, filter: 'blur(16px)' }, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.9, ease: 'power2.out' }, '-=0.6')
      .fromTo('#h-act', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out' }, '-=0.5')
      .to('#h-sc', { opacity: 1, duration: 0.8, ease: 'power2.out' }, '-=0.3');
  }

  /* BLUR-TO-SHARP SCROLL REVEALS
     Explicit paused tween + onEnter so we can also force-play elements that are
     already in view at load (above-the-fold heroes on inner pages) — a plain
     embedded scrollTrigger never fires for elements past their start on load. */
  const skipIds = new Set(['h-h1', 'h-sub', 'h-act', 'h-sc']);
  const reveals = [];
  gsap.utils.toArray('.rv').forEach(el => {
    if (skipIds.has(el.id)) return;
    const tw = gsap.fromTo(el,
      { opacity: 0, filter: 'blur(22px)', y: 34 },
      { opacity: 1, filter: 'blur(0px)', y: 0, duration: 1.1, ease: 'power3.out', paused: true });
    const st = ScrollTrigger.create({ trigger: el, start: 'top 88%', onEnter: () => tw.play() });
    reveals.push({ tw, st });
  });
  // Reveal anything already at/above the fold (progress > 0 means start is passed).
  function playRevealsInView() {
    reveals.forEach(({ tw, st }) => { if (st.progress > 0 && tw.paused()) tw.play(); });
  }

  /* STATEMENT NUMBER */
  if (document.querySelector('#st-num')) {
    gsap.fromTo('#st-num',
      { opacity: 0, scale: 1.14, filter: 'blur(32px)' },
      { opacity: 1, scale: 1, filter: 'blur(0px)', duration: 1.3, ease: 'power3.out',
        scrollTrigger: { trigger: '#st-num', start: 'top 85%' } }
    );
  }

  /* STAGGER GRIDS — paused-tween + onEnter so already-in-view containers on inner
     pages animate in on load (embedded scrollTrigger never fires for elements past
     their start on load — same gotcha as .rv reveals above). */
  gsap.utils.toArray('[data-stagger]').forEach(container => {
    const items = container.querySelectorAll('[data-stagger-item]');
    if (!items.length) return;
    const tw = gsap.fromTo(items,
      { opacity: 0, filter: 'blur(14px)', y: 28, scale: 0.97 },
      { opacity: 1, filter: 'blur(0px)', y: 0, scale: 1,
        duration: 0.8, stagger: 0.1, ease: 'power2.out', paused: true });
    const st = ScrollTrigger.create({ trigger: container, start: 'top 85%', onEnter: () => tw.play() });
    reveals.push({ tw, st });
  });

  /* PARALLAX */
  if (document.querySelector('#mid-video-bg')) {
    gsap.to('#mid-video-bg', { yPercent: 18, ease: 'none',
      scrollTrigger: { trigger: '#mid-video', start: 'top bottom', end: 'bottom top', scrub: true } });
  }
  if (document.querySelector('#ring-video-wrap')) {
    gsap.to('#ring-video-wrap', { yPercent: 20, ease: 'none',
      scrollTrigger: { trigger: '#ring-section', start: 'top bottom', end: 'bottom top', scrub: true } });
  }

  /* COUNT-UP */
  gsap.utils.toArray('[data-count]').forEach(el => {
    const target = parseInt(el.dataset.count);
    const suffix = target === 100 ? '%' : '';
    ScrollTrigger.create({
      trigger: el, start: 'top 85%', once: true,
      onEnter() {
        gsap.fromTo({ val: 0 }, { val: target }, {
          duration: 1.4, ease: 'power2.out',
          onUpdate() { el.textContent = Math.round(this.targets()[0].val) + suffix; }
        });
      }
    });
  });

  /* NAV SCROLL BEHAVIOR — frosted glass gets more opaque past 60px (theme-aware via CSS) */
  const nav = document.querySelector('.nav');
  if (nav) {
    ScrollTrigger.create({
      start: 'top -60',
      onUpdate: self => {
        nav.classList.toggle('is-scrolled', self.progress > 0);
      }
    });
  }

  /* Recompute trigger positions after late layout shifts (web fonts swapping in,
     section CSS that loads via a <link> in the body on inner pages). Without this,
     triggers measured against the fallback-font/unstyled layout stay stale and
     reveals can get stuck at opacity:0. */
  window.addEventListener('load', () => { ScrollTrigger.refresh(); playRevealsInView(); });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(() => { ScrollTrigger.refresh(); playRevealsInView(); });
  }
  // Also run once on next frame for the common fast-load case.
  requestAnimationFrame(() => { ScrollTrigger.refresh(); playRevealsInView(); });
})();
